// @ts-nocheck
import cn from 'classnames';
import { capitalize } from 'lodash';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { ReportingCommentSection } from '@/modules/reporting/ReportingCommentSection';
import { getAdvancedFiltersEnabled } from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ReportingSectionAdvancedFilterStyled, SectionHeaderStyled } from './sectionHeader.styles';

type Props = {
  children: React.ReactNode;
  className?: string;
  downloadButton?: React.ReactElement;
  hideAdvancedFilters?: boolean;
  hideComments?: boolean;
  section: string;
};

type State = {
  openSection?: string;
  savedComment?: string;
};

function SectionHeader({
  children,
  className,
  downloadButton,
  hideAdvancedFilters = false,
  hideComments = false,
  section
}: Props) {
  const [{ openSection, savedComment }, setState] = useState<State>({});
  const { formatMessage } = useIntl();

  const advancedFiltersEnabled = useSelector(getAdvancedFiltersEnabled);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);

  const advancedFilterPermissions = checkUserResourcePermissions(
    `reporting${capitalize(section)}SectionAdvancedFilter`,
    user,
    userRoles
  );

  const readCommentPermissions = checkUserResourcePermissions('reportingReadComments', user, userRoles);
  const writeCommentPermissions = checkUserResourcePermissions('reportingWriteComments', user, userRoles);

  const viewCommentSection = !hideComments && (readCommentPermissions || writeCommentPermissions);
  const viewCommentSectionButton = viewCommentSection && (writeCommentPermissions || savedComment);

  const handleSectionCommentChange = (commentData: string) => {
    setState(prevState => ({
      ...prevState,
      savedComment: commentData
    }));
  };

  const handleSectionCommentCancel = () => {
    setState(prevState => ({
      ...prevState,
      openSection: undefined
    }));
  };

  const handleAdvancedFilterButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      openSection: openSection === 'advancedFilter' ? undefined : 'advancedFilter'
    }));
  };

  const handleCommentsButtonClick = () => {
    setState(prevState => ({
      ...prevState,
      openSection: openSection === 'comments' ? undefined : 'comments'
    }));
  };

  const closeIconClassName = 'sectionHeader__buttonFilters-closeIcon';

  const mainComponentClassName =
    className &&
    cn('sectionHeader', {
      [className]: true
    });

  const commentButtonIconClassName = cn('far fa-comment-lines', {
    badge: savedComment && openSection !== 'comments'
  });

  const commentCloseButtonIconClassName = cn(closeIconClassName, {
    [`${closeIconClassName}--`]: openSection === 'comments',
    [`${closeIconClassName}--hide`]: openSection !== 'comments'
  });

  const advancedFilterCloseButtonIconClassName = cn(closeIconClassName, {
    [`${closeIconClassName}--`]: openSection === 'advancedFilter',
    [`${closeIconClassName}--hide`]: openSection !== 'advancedFilter'
  });

  return (
    <SectionHeaderStyled className={mainComponentClassName}>
      <div className="sectionHeader__header">
        {children}
        <span className="sectionHeader__buttonContainer">
          {advancedFilterPermissions && !hideAdvancedFilters && (
            <ButtonUI
              className="sectionHeader__buttonFilters"
              icons={[
                {
                  icon: 'far fa-filter'
                },
                {
                  className: advancedFilterCloseButtonIconClassName,
                  icon: 'far fa-times'
                }
              ]}
              onClick={handleAdvancedFilterButtonClick}
              shape="squareRounded"
              size="medium"
              text={formatMessage({
                defaultMessage: 'Advanced filter',
                id: 'reporting.sectionHeader.filterButton.label'
              })}
              type="secondary"
              visible
            />
          )}
          {viewCommentSectionButton && (
            <ButtonUI
              badge={!!savedComment}
              className="sectionHeader__buttonComments"
              icons={[
                {
                  icon: commentButtonIconClassName
                },
                {
                  className: commentCloseButtonIconClassName,
                  icon: 'far fa-times'
                }
              ]}
              onClick={handleCommentsButtonClick}
              shape="squareRounded"
              size="medium"
              text={formatMessage({
                defaultMessage: 'Comments',
                id: 'reporting.sectionHeader.commentsButton.label'
              })}
              type="secondary"
              visible
            />
          )}
        </span>
        {downloadButton}
      </div>
      {openSection === 'advancedFilter' ? (
        <div className="sectionHeader__content">
          <ReportingSectionAdvancedFilterStyled enabled={advancedFiltersEnabled} section={section} />
        </div>
      ) : null}
      {viewCommentSection && (
        <ReportingCommentSection
          hide={openSection !== 'comments'}
          onCancel={handleSectionCommentCancel}
          onChange={handleSectionCommentChange}
          onlyView={!writeCommentPermissions}
          section={section}
        />
      )}
    </SectionHeaderStyled>
  );
}

export default SectionHeader;
